/**
 * Vault-app user settings hook.
 *
 * A lean, single-project variant of the advisor app's `useUserSettingsQuery`.
 * The advisor version gates the query on an active org id (`!!uid && !!orgId`);
 * the vault population has no org context, so this variant gates on the Firebase
 * uid alone.
 */
import type { UpdateUserRequest, User } from "@secure-vault/api/types";
import { getUser as apiGetUser, updateUser } from "@secure-vault/api";
import {
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { auth } from "@/lib/firebase";

const baseKey = ["vault-user-settings"] as const;
const key = (uid?: string | null): QueryKey => [...baseKey, uid ?? "anonymous"];

export function useUserSettings() {
  const uid = auth.currentUser?.uid;
  return useQuery<User | null, Error>({
    queryKey: key(uid),
    queryFn: async (): Promise<User | null> => {
      if (!uid) return null;
      const data = await apiGetUser();
      return (data as User) ?? null;
    },
    enabled: !!uid,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateUserSettings() {
  const client = useQueryClient();
  const uid = auth.currentUser?.uid;
  return useMutation<User, Error, UpdateUserRequest>({
    mutationFn: async (data: UpdateUserRequest) => {
      const updated = await updateUser(data);
      return updated as User;
    },
    onSuccess: (data: User) => {
      if (data) client.setQueryData(key(uid), data);
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: key(uid) });
    },
  });
}
